import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  feedUrl,
  formatDuration,
  useCreateEpisode,
  useDeleteShow,
  useRefreshFeed,
  useShow,
  useShowEpisodes,
  useUpdateShow,
  type Show,
  type ShowEpisode,
} from '../lib/shows';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { TextField } from '../components/ui/Field';
import { EmptyState, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { ShowFormDialog } from '../components/shows/ShowFormDialog';
import { CopyField, StatusBadge } from '../components/shows/bits';
import { CoverArt } from '../components/shows/CoverArt';
import { Analytics } from '../components/shows/Analytics';
import { Artwork } from './PodcastsPage';

export function PodcastDetailPage() {
  const { podcastId } = useParams<{ podcastId: string }>();
  const show = useShow(podcastId);
  const episodes = useShowEpisodes(podcastId);

  if (show.isLoading) return <Loading />;
  if (show.isError || !show.data) return <ErrorMessage error={show.error} onRetry={() => show.refetch()} />;

  return <ShowView show={show.data} episodes={episodes} />;
}

function ShowView({ show, episodes }: { show: Show; episodes: ReturnType<typeof useShowEpisodes> }) {
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const update = useUpdateShow(show.id);
  const remove = useDeleteShow(show.id);
  const navigate = useNavigate();
  const toast = useToast();
  const list = episodes.data?.episodes ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start gap-4">
        <Artwork show={show} size="size-20" />
        <div className="min-w-0 flex-1">
          <PageHeader
            title={show.title}
            description={show.author ? `by ${show.author}` : undefined}
            actions={
              <>
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit show
                </Button>
                <Button onClick={() => setCreating(true)}>New episode</Button>
              </>
            }
          />
        </div>
      </div>

      <section aria-labelledby="episodes-heading" className="space-y-3">
        <h2 id="episodes-heading" className="text-lg font-semibold">
          Episodes
        </h2>
        {episodes.isLoading && <Loading />}
        {episodes.isError && <ErrorMessage error={episodes.error} onRetry={() => episodes.refetch()} />}
        {episodes.data && list.length === 0 && (
          <EmptyState
            title="No episodes yet"
            body="Create an episode, upload its audio and publish — it goes out on your RSS feed."
            action={<Button onClick={() => setCreating(true)}>New episode</Button>}
          />
        )}
        {list.length > 0 && <EpisodeTable showId={show.id} episodes={list} />}
      </section>

      <Analytics podcastId={show.id} />

      <CoverArt show={show} />

      <Distribution show={show} episodes={list} />

      <section className="rounded-xl border border-danger-soft p-4">
        <h2 className="text-sm font-semibold text-danger">Delete show</h2>
        <p className="mt-1 text-sm text-muted">Removes the show, its episodes and its feed. Uploaded audio stays in your library.</p>
        <Button variant="danger" size="sm" className="mt-3" onClick={() => setDeleting(true)}>
          Delete show
        </Button>
      </section>

      {editing && (
        <ShowFormDialog
          open
          show={show}
          onClose={() => setEditing(false)}
          submitting={update.isPending}
          error={update.error}
          onSubmit={(values) =>
            update.mutate(
              {
                title: values.title,
                author: values.author || undefined,
                description: values.description || undefined,
                category: values.category || undefined,
                language: values.language || 'en',
                explicit: values.explicit,
                ownerEmail: values.ownerEmail || null,
              },
              {
                onSuccess: () => {
                  setEditing(false);
                  toast.show('Show updated', 'success');
                },
              },
            )
          }
        />
      )}
      {creating && <NewEpisodeDialog showId={show.id} nextNumber={nextEpisodeNumber(list)} onClose={() => setCreating(false)} />}
      <ConfirmDialog
        open={deleting}
        danger
        title={`Delete “${show.title}”?`}
        body="This can’t be undone. Listeners subscribed to the feed will stop getting new episodes."
        confirmLabel="Delete show"
        busy={remove.isPending}
        onCancel={() => setDeleting(false)}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => {
              toast.show('Show deleted', 'success');
              navigate('/podcasts');
            },
            onError: () => toast.show('Could not delete the show', 'error'),
          })
        }
      />
    </div>
  );
}

export function nextEpisodeNumber(episodes: ShowEpisode[]): number {
  return episodes.reduce((max, e) => Math.max(max, e.episodeNumber ?? 0), 0) + 1;
}

function EpisodeTable({ showId, episodes }: { showId: string; episodes: ShowEpisode[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface text-xs uppercase tracking-wide text-muted">
          <tr>
            <th className="px-4 py-2 font-medium">Episode</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Length</th>
            <th className="px-4 py-2 font-medium">Published</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {episodes.map((e) => (
            <tr key={e.id} className="hover:bg-surface">
              <td className="px-4 py-3">
                <Link to={`/podcasts/${showId}/episodes/${e.id}`} className="font-medium text-text hover:text-accent">
                  {e.episodeNumber ? <span className="mr-2 text-muted">#{e.episodeNumber}</span> : null}
                  {e.title}
                </Link>
                {!e.mediaFileId && <div className="text-xs text-warning">No audio yet</div>}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={e.status} />
              </td>
              <td className="px-4 py-3 text-muted">{formatDuration(e.durationMs)}</td>
              <td className="px-4 py-3 text-muted">{e.publishedAt ? new Date(e.publishedAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewEpisodeDialog({ showId, nextNumber, onClose }: { showId: string; nextNumber: number; onClose: () => void }) {
  const create = useCreateEpisode(showId);
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | undefined>();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Give the episode a title.');
      return;
    }
    create.mutate(
      { title: title.trim(), episodeNumber: nextNumber },
      { onSuccess: (episode) => navigate(`/podcasts/${showId}/episodes/${episode.id}`) },
    );
  };

  return (
    <Dialog open onClose={onClose} title="New episode" description={`Episode #${nextNumber}. You can add audio next.`}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} error={error} maxLength={200} autoFocus />
        {create.error !== null && <ErrorMessage error={create.error} />}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create episode
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export interface ReadinessItem {
  label: string;
  done: boolean;
  hint: string;
}

/** What Apple Podcasts / Spotify need before a feed is accepted. */
export function feedReadiness(show: Show, episodes: ShowEpisode[]): ReadinessItem[] {
  return [
    { label: 'Show name', done: !!show.title, hint: 'Required by every directory.' },
    { label: 'Description', done: !!show.description?.trim(), hint: 'Edit the show to add one.' },
    { label: 'Host or company', done: !!show.author?.trim(), hint: 'Shown as the author.' },
    { label: 'Category', done: !!show.category, hint: 'Apple requires one.' },
    { label: 'Cover art', done: !!show.artworkUrl, hint: 'Square, 1400–3000 px. Apple requires it.' },
    { label: 'Owner email', done: !!show.ownerEmail, hint: 'Edit the show to add one; used to verify ownership.' },
    {
      label: 'A published episode with audio',
      done: episodes.some((e) => e.status === 'published' && e.mediaFileId),
      hint: 'Directories reject empty feeds.',
    },
  ];
}

function Distribution({ show, episodes }: { show: Show; episodes: ShowEpisode[] }) {
  const refresh = useRefreshFeed(show.id);
  const toast = useToast();
  const items = feedReadiness(show, episodes);
  const ready = items.every((i) => i.done);

  return (
    <section aria-labelledby="distribution-heading" className="space-y-4 rounded-xl border border-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="distribution-heading" className="text-lg font-semibold">
          Distribution
        </h2>
        <Button
          variant="secondary"
          size="sm"
          loading={refresh.isPending}
          onClick={() =>
            refresh.mutate(undefined, {
              onSuccess: () => toast.show('Feed refresh queued — it updates within a minute', 'success'),
              onError: () => toast.show('Could not refresh the feed', 'error'),
            })
          }
        >
          Refresh feed
        </Button>
      </div>
      <CopyField label="RSS feed URL" value={feedUrl(show.id)} />
      <div>
        <h3 className="text-sm font-medium">
          {ready ? 'Ready to submit to Apple Podcasts and Spotify' : 'Before submitting to Apple Podcasts and Spotify'}
        </h3>
        <ul className="mt-2 space-y-1.5">
          {items.map((i) => (
            <li key={i.label} className="flex items-start gap-2 text-sm">
              <span aria-hidden className={i.done ? 'text-success' : 'text-muted'}>
                {i.done ? '✓' : '○'}
              </span>
              <span>
                <span className={i.done ? 'text-text' : 'text-muted'}>{i.label}</span>
                {!i.done && <span className="block text-xs text-muted">{i.hint}</span>}
              </span>
              <span className="sr-only">{i.done ? 'done' : 'to do'}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
